import { useState } from "react";
import {
  Page,
  Card,
  TextField,
  Button,
  BlockStack,
  Text,
  Banner,
  InlineGrid,
  RadioButton,
  Link,
} from "@shopify/polaris";
import { api, ApiRequestError } from "../api";
import posthog from "../posthog";
import type { Printer } from "../types";

interface Props {
  onComplete: () => void;
}

export default function Onboarding({ onComplete }: Props): JSX.Element {
  const [easypostKey, setEasypostKey] = useState("");
  const [easypostSaved, setEasypostSaved] = useState(false);
  const [easypostChecked, setEasypostChecked] = useState<{ valid: boolean; error?: string } | null>(null);

  const [printnodeKey, setPrintnodeKey] = useState("");
  const [printers, setPrinters] = useState<Printer[] | null>(null);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPrintSent, setTestPrintSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function verifyEasyPost(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      setEasypostChecked(await api.post<{ valid: boolean; error?: string }>("/verify-easypost", { apiKey: easypostKey }));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not reach EasyPost");
    } finally {
      setLoading(false);
    }
  }

  async function saveEasyPost(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      await api.patch("/settings/easypost", { apiKey: easypostKey });
      posthog.capture("onboarding_easypost_connected");
      setEasypostSaved(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not save EasyPost key");
    } finally {
      setLoading(false);
    }
  }

  async function verifyPrintNode(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      await api.post("/credentials/printnode/verify", { api_key: printnodeKey });
      const { printers: fetched } = await api.get<{ printers: Printer[] }>("/printers");
      posthog.capture("onboarding_printnode_connected", { printer_count: fetched.length });
      setPrinters(fetched);
      if (fetched.length > 0) setSelectedPrinterId(String(fetched[0].id));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not verify PrintNode key");
    } finally {
      setLoading(false);
    }
  }

  // A test print is how a merchant confirms the printer, not a condition of
  // being set up. It used to run inside finish(), before onComplete(), so an
  // offline printer or a PrintNode blip left the merchant in the wizard with
  // the default printer already saved — the app's own gate condition met, the
  // in-session state disagreeing, and no way forward but a page reload nobody
  // thinks to try. Separate button, separate outcome, never blocks.
  async function sendTestPrint(): Promise<void> {
    if (!selectedPrinterId) return;
    setTesting(true);
    setError(null);
    setTestPrintSent(false);
    try {
      await api.post("/printers/default", { printer_id: selectedPrinterId });
      await api.post(`/printers/${selectedPrinterId}/test-print`);
      posthog.capture("test_print_requested", { source: "onboarding" });
      setTestPrintSent(true);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? `Test print failed: ${err.body.message}. You can still finish setup and try again from Settings.`
          : "Could not send the test print. You can still finish setup and try again from Settings."
      );
    } finally {
      setTesting(false);
    }
  }

  async function finish(): Promise<void> {
    if (!selectedPrinterId) return;
    setLoading(true);
    setError(null);
    try {
      // The only thing setup actually requires: the printer the app will print
      // every label to. App.tsx gates the wizard on this being present.
      await api.post("/printers/default", { printer_id: selectedPrinterId });
      posthog.capture("onboarding_completed");
      onComplete();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not finish setup");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Page title="Welcome to ShipPromise" narrowWidth>
      <BlockStack gap="400">
        {error && <Banner tone="critical">{error}</Banner>}

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Step 1. Connect EasyPost
            </Text>
            <Text as="p" tone="subdued">
              ShipPromise buys postage through EasyPost, which compares live rates across USPS, UPS,
              FedEx and DHL and buys the cheapest one. Create a key in your{" "}
              <Link url="https://www.easypost.com/account/api-keys" external>
                EasyPost API keys
              </Link>{" "}
              page.
            </Text>
            <TextField
              label="EasyPost API key"
              value={easypostKey}
              onChange={(value) => {
                setEasypostKey(value);
                setEasypostChecked(null);
                setEasypostSaved(false);
              }}
              autoComplete="off"
              type="password"
              disabled={easypostSaved}
            />

            {easypostChecked && !easypostChecked.valid && (
              <Banner tone="critical">{easypostChecked.error ?? "EasyPost rejected this API key"}</Banner>
            )}
            {easypostChecked?.valid && !easypostSaved && <Banner tone="success">Key verified.</Banner>}
            {easypostSaved && <Banner tone="success">EasyPost connected.</Banner>}

            {!easypostSaved && (
              <InlineGrid columns={2} gap="200">
                <Button onClick={verifyEasyPost} loading={loading} disabled={!easypostKey}>
                  Verify
                </Button>
                <Button
                  variant="primary"
                  onClick={saveEasyPost}
                  loading={loading}
                  disabled={!easypostChecked?.valid}
                >
                  Save and continue
                </Button>
              </InlineGrid>
            )}
          </BlockStack>
        </Card>

        {easypostSaved && (
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Step 2. Connect PrintNode
              </Text>
              <Text as="p" tone="subdued">
                PrintNode lets ShipPromise print labels directly to your shipping station printer,
                anywhere in the world, without a print dialog.
              </Text>
              <TextField
                label="PrintNode API key"
                value={printnodeKey}
                onChange={setPrintnodeKey}
                autoComplete="off"
                type="password"
              />
              {printers === null && (
                // "Save and continue", matching step 1's primary, because that
                // is what this does: /credentials/printnode/verify stores the
                // key as well as checking it. Labelling it "Verify key" implied
                // a separate save step that does not exist, so a merchant who
                // stopped here reasonably believed nothing had been kept.
                <Button variant="primary" onClick={verifyPrintNode} loading={loading} disabled={!printnodeKey}>
                  Save and continue
                </Button>
              )}

              {printers !== null && (
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    Choose your printer
                  </Text>
                  <Text as="p" tone="subdued">
                    ShipPromise prints every label to this printer. You can change it later in Settings.
                  </Text>

                  {printers.length === 0 ? (
                    <Banner tone="warning">
                      No printers found on this PrintNode account. Install the PrintNode client on
                      your shipping computer, then reload this page.
                    </Banner>
                  ) : (
                    <InlineGrid columns={1} gap="200">
                      {printers.map((printer) => (
                        <RadioButton
                          key={printer.id}
                          label={`${printer.name} (${printer.state})`}
                          checked={selectedPrinterId === String(printer.id)}
                          id={`printer-${printer.id}`}
                          name="printer"
                          // Clearing the banner matters: it asserts a test
                          // print landed, and left standing after the merchant
                          // picks a different printer it vouches for hardware
                          // that was never sent anything.
                          onChange={() => {
                            setSelectedPrinterId(String(printer.id));
                            setTestPrintSent(false);
                            setError(null);
                          }}
                        />
                      ))}
                    </InlineGrid>
                  )}

                  {testPrintSent && (
                    <Banner tone="success">
                      Test print sent. Check the printer — if nothing came out, finish setup anyway and adjust the
                      printer in Settings.
                    </Banner>
                  )}

                  <InlineGrid columns={2} gap="200">
                    <Button onClick={sendTestPrint} loading={testing} disabled={!selectedPrinterId}>
                      Send test print
                    </Button>
                    <Button variant="primary" onClick={finish} loading={loading} disabled={!selectedPrinterId}>
                      Finish setup
                    </Button>
                  </InlineGrid>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
